import { create } from "zustand";
import { persist } from "zustand/middleware";
import { track } from "@/lib/analytics";

interface Unlocks {
  konami: boolean;
  whoami: boolean;
  cubeSolved: boolean;
  devMode: boolean;
}

interface ProgressData {
  visitCount: number;
  totalActiveSeconds: number;
  plantStage: 0 | 1 | 2 | 3 | 4;
  unlocks: Unlocks;
  lastVisitISO: string;
}

interface ProgressStore extends ProgressData {
  recordVisit: () => void;
  addActiveSecond: () => void;
  setPlantStage: (s: 0 | 1 | 2 | 3 | 4) => void;
  unlock: (key: keyof Unlocks) => void;
  reset: () => void;
}

const DEFAULT: ProgressData = {
  visitCount: 0,
  totalActiveSeconds: 0,
  plantStage: 0,
  unlocks: { konami: false, whoami: false, cubeSolved: false, devMode: false },
  lastVisitISO: "",
};

const computeStage = (
  visitCount: number,
  totalActiveSeconds: number,
  anyUnlock: boolean,
): 0 | 1 | 2 | 3 | 4 => {
  if (visitCount >= 5 || anyUnlock) return 4;
  if (totalActiveSeconds >= 300 || visitCount >= 3) return 3;
  if (totalActiveSeconds >= 60) return 2;
  if (visitCount >= 1) return 1;
  return 0;
};

export const useProgressStore = create<ProgressStore>()(
  persist(
    (set) => ({
      ...DEFAULT,
      recordVisit: () =>
        set((s) => {
          const visitCount = s.visitCount + 1;
          const anyUnlock = Object.values(s.unlocks).some(Boolean);
          return {
            visitCount,
            lastVisitISO: new Date().toISOString(),
            plantStage: computeStage(
              visitCount,
              s.totalActiveSeconds,
              anyUnlock,
            ),
          };
        }),
      addActiveSecond: () =>
        set((s) => {
          const totalActiveSeconds = s.totalActiveSeconds + 1;
          const anyUnlock = Object.values(s.unlocks).some(Boolean);
          return {
            totalActiveSeconds,
            plantStage: computeStage(
              s.visitCount,
              totalActiveSeconds,
              anyUnlock,
            ),
          };
        }),
      setPlantStage: (plantStage) => set({ plantStage }),
      unlock: (key) =>
        set((s) => {
          const unlocks = { ...s.unlocks, [key]: true };
          track("easter_egg_unlock", { name: key });
          return {
            unlocks,
            plantStage: computeStage(s.visitCount, s.totalActiveSeconds, true),
          };
        }),
      reset: () => set(DEFAULT),
    }),
    { name: "akotlorz:progress:v1" },
  ),
);
